import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Checkbox, Input, Modal, Select } from "semantic-ui-react";
import { DateInput } from "../../components/DateInput/DateInput";
import { constructDate, dateInit } from "../../models/common";
import { idDataSetsFilterInit, IIDDataSetFilter } from "../../models/crewMember";
import { IExaminationOrganization } from "../../models/examinationOrganization";
import { IPaymentType } from "../../models/paymentType";
import { AppState } from "../../store/configureStore";

interface Props {
    open: boolean;
    handleClose: () => void;
    queryFilter: IIDDataSetFilter;
    setQueryFilter: Dispatch<SetStateAction<IIDDataSetFilter>>;
}

export const IcsAplicationsAdvanceFilterForm = ({
    open,
    queryFilter,
    setQueryFilter,
    handleClose,
}: Props) => {
    const intl = useIntl();
    const [dateOfBirth, setDateOfBirth] = useState(dateInit);
    const [dateOfReceiptFrom, setDateOfReceiptFrom] = useState(dateInit);
    const [dateOfReceiptTo, setDateOfReceiptTo] = useState(dateInit);
    const [dateOfIssueFrom, setDateOfIssueFrom] = useState(dateInit);
    const [dateOfIssueTo, setDateOfIssueTo] = useState(dateInit);
    const [localFilter, setLocalFilter] = useState(idDataSetsFilterInit);

    const handleInputChange = (e: any) =>
        setLocalFilter({ ...localFilter, [e.target.name]: e.target.value });

    const handleKeyPress = (e: any) => {
        if (e.key === "Enter" || e.charCode === 13) {
            setLocalFilter(localFilter);
        }
    };

    const examinationOrganizations = useSelector(
        (state: AppState) => state.examinationOrganizations
    );
    const examinationOrPaymentToDropdownProp = (
        items: IExaminationOrganization[] | IPaymentType[]
    ) => {
        return items
            .filter(x => !x.disabled)
            .map(x => {
                return {
                    key: x.guid,
                    text: x.name,
                    value: x.guid,
                };
            });
    };

    useEffect(() => {
        setLocalFilter(queryFilter);
    }, [open]);

    const startSearch = () => {
        setQueryFilter(localFilter);
        handleClose();
    };

    useEffect(() => {
        setLocalFilter(s => ({ ...s, dateOfBirth: constructDate(dateOfBirth) }));
    }, [dateOfBirth, setLocalFilter]);

    useEffect(() => {
        setLocalFilter(s => ({ ...s, dateOfReceiptFrom: constructDate(dateOfReceiptFrom) }));
    }, [dateOfReceiptFrom, setLocalFilter]);

    useEffect(() => {
        setLocalFilter(s => ({ ...s, dateOfReceiptTo: constructDate(dateOfReceiptTo) }));
    }, [dateOfReceiptTo, setLocalFilter]);

    useEffect(() => {
        setLocalFilter(s => ({ ...s, dateOfIssueFrom: constructDate(dateOfIssueFrom) }));
    }, [dateOfIssueFrom, setLocalFilter]);

    useEffect(() => {
        setLocalFilter(s => ({ ...s, dateOfIssueTo: constructDate(dateOfIssueTo) }));
    }, [dateOfIssueTo, setLocalFilter]);

    return (
        <>
            <Modal
                open={open}
                size="small"
                closeIcon
                className="advance-search-modal"
                onClose={handleClose}
            >
                <Modal.Header>
                    <span>{intl.formatMessage({ id: "advanceSearch" })}</span>
                    <Button
                        primary
                        onClick={() => setLocalFilter(idDataSetsFilterInit)}
                        content={intl.formatMessage({ id: "restardFileds" })}
                    />
                </Modal.Header>
                <Modal.Content>
                    <div>
                        <section className="section-row-new">
                            <label>{intl.formatMessage({ id: "lastName" })}:</label>
                            <Input
                                name="lastName"
                                type="text"
                                value={localFilter.lastName}
                                onChange={handleInputChange}
                                onKeyPress={(e: any) =>
                                    localFilter.firstName.length > 0 && handleKeyPress(e)
                                }
                                autoComplete="off"
                            />
                        </section>
                        <section className="section-row-new">
                            <label>{intl.formatMessage({ id: "firstName" })}:</label>
                            <Input
                                name="firstName"
                                value={localFilter.firstName}
                                onChange={handleInputChange}
                                onKeyPress={(e: any) =>
                                    localFilter.lastName.length > 0 && handleKeyPress(e)
                                }
                                autoComplete="off"
                            />
                        </section>
                        <section className="section-row-new">
                            <label>{intl.formatMessage({ id: "dateOfBirth" })}:</label>
                            <DateInput date={dateOfBirth} setDate={setDateOfBirth} clearable />
                        </section>
                        <section className="section-row-new">
                            <label>{intl.formatMessage({ id: "examinationOrganization" })}:</label>
                            <Select
                                className="section-row-new"
                                selectOnBlur={false}
                                search
                                options={examinationOrPaymentToDropdownProp(
                                    examinationOrganizations
                                )}
                                value={localFilter.examinationOrganization}
                                onChange={(e, data: any) =>
                                    setLocalFilter({
                                        ...localFilter,
                                        examinationOrganization: data.value,
                                    })
                                }
                                onKeyPress={(e: any) =>
                                    localFilter.examinationOrganization.length > 0 &&
                                    handleKeyPress(e)
                                }
                            />
                        </section>
                    </div>
                    <div>
                        <form className="add-user-form">
                            <section>
                                <label>{intl.formatMessage({ id: "dateOfReceiptFrom" })}:</label>
                                <DateInput
                                    date={dateOfReceiptFrom}
                                    setDate={setDateOfReceiptFrom}
                                    clearable
                                />
                            </section>
                            <section>
                                <label>{intl.formatMessage({ id: "dateOfReceiptTo" })}:</label>
                                <DateInput
                                    date={dateOfReceiptTo}
                                    setDate={setDateOfReceiptTo}
                                    clearable
                                />
                            </section>
                            <section>
                                <label>{intl.formatMessage({ id: "dateOfIssueFrom" })}:</label>
                                <DateInput
                                    date={dateOfIssueFrom}
                                    setDate={setDateOfIssueFrom}
                                    clearable
                                />
                            </section>

                            <section>
                                <label>{intl.formatMessage({ id: "dateOfIssueTo" })}:</label>
                                <DateInput
                                    date={dateOfIssueTo}
                                    setDate={setDateOfIssueTo}
                                    clearable
                                />
                            </section>
                        </form>
                        <section className="section-row-new">
                            <label>{intl.formatMessage({ id: "applicationCategory" })}:</label>
                            <Checkbox
                                label={{ children: intl.formatMessage({ id: "newIssue" }) }}
                                checked={localFilter.applicationCategoryNewIssue}
                                onChange={(e, data) => {
                                    setLocalFilter({...localFilter, applicationCategoryNewIssue: data.checked!})
                                }}
                            />
                            <Checkbox
                                label={{ children: intl.formatMessage({ id: "after2012" }) }}
                                checked={localFilter.applicationCategoryAfter}
                                onChange={(e, data) => {
                                    setLocalFilter({...localFilter, applicationCategoryAfter: data.checked!})
                                }}
                            />
                            <Checkbox
                                label={{ children: intl.formatMessage({ id: "before2012" }) }}
                                checked={localFilter.applicationCategoryBefore}
                                onChange={(e, data) => {
                                    setLocalFilter({...localFilter, applicationCategoryBefore: data.checked!})
                                }}
                            />
                        </section>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        primary
                        onClick={startSearch}
                        content={intl.formatMessage({ id: "searchInAdv" })}
                        fluid
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};
